import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    fetchSupportInfo,
    sendSupportChat,
    sendSupportContact,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
    ArrowLeft,
    Send,
    Mail,
    Phone,
    Bot,
    User as UserIcon,
    Loader2,
    Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STARTER = [
    { id: "billing", label: "Billing question" },
    { id: "pause", label: "How do I pause?" },
    { id: "referral", label: "How does referral work?" },
    { id: "recipes", label: "Are recipes shipped?" },
];

const SupportPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [info, setInfo] = useState(null);
    const [tab, setTab] = useState("chat"); // chat | email
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "hey, I'm FlexBot 💪 — your support sidekick. ask me anything about subscriptions, billing, recipes, or referrals.",
        },
    ]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);

    // Email tab state
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const scrollRef = useRef(null);

    useEffect(() => {
        fetchSupportInfo().then(setInfo).catch(() => {});
    }, []);

    useEffect(() => {
        scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
        });
    }, [messages, sending]);

    const send = async (text) => {
        const txt = (text ?? input).trim();
        if (!txt || sending) return;
        const next = [...messages, { role: "user", content: txt }];
        setMessages(next);
        setInput("");
        setSending(true);
        try {
            const r = await sendSupportChat(sessionId, next);
            setSessionId(r.session_id);
            setMessages((m) => [...m, { role: "assistant", content: r.reply }]);
        } catch (e) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        "something glitched on my end. try again, or email " +
                        (info?.email || "support") +
                        " if it keeps happening.",
                },
            ]);
        } finally {
            setSending(false);
        }
    };

    const submitEmail = async () => {
        if (!user) {
            toast.error("Sign in to send a message");
            navigate("/login", { state: { next: "/support" } });
            return;
        }
        if (!subject.trim() || !body.trim()) {
            toast.error("Add a subject and message first");
            return;
        }
        setSubmitting(true);
        try {
            const r = await sendSupportContact(subject, body);
            toast.success(r.delivered ? "Message sent" : "Message logged — we'll be in touch");
            setSubject("");
            setBody("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="support-page" className="min-h-[100dvh] bg-[#0A0A0A] text-white pb-32">
            <header className="px-5 pt-6 pb-3 flex items-center gap-3 border-b border-neutral-900">
                <button
                    data-testid="back-button"
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"
                >
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01] mb-0.5">
                        Support
                    </p>
                    <h1 className="font-display text-3xl uppercase leading-none">FlexBot</h1>
                </div>
            </header>

            {/* Tab toggle */}
            <div className="px-5 pt-4 pb-2 flex gap-2">
                <button
                    data-testid="tab-chat"
                    onClick={() => setTab("chat")}
                    className={`flex-1 py-2.5 rounded-md text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 transition ${
                        tab === "chat"
                            ? "bg-[#E1FF01] text-[#0A0A0A]"
                            : "bg-white/5 border border-white/10 text-white"
                    }`}
                >
                    <Bot size={14} /> Ask AI
                </button>
                <button
                    data-testid="tab-email"
                    onClick={() => setTab("email")}
                    className={`flex-1 py-2.5 rounded-md text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-1.5 transition ${
                        tab === "email"
                            ? "bg-[#E1FF01] text-[#0A0A0A]"
                            : "bg-white/5 border border-white/10 text-white"
                    }`}
                >
                    <Mail size={14} /> Email / Call
                </button>
            </div>

            {/* Quick contact cards always visible */}
            <div className="px-5 pt-3 pb-2 flex gap-2">
                {info?.email && (
                    <a
                        data-testid="support-mailto"
                        href={`mailto:${info.email}`}
                        className="flex-1 bg-[#141414] border border-neutral-800 rounded-xl p-3 hover:border-[#E1FF01]/40 transition flex items-center gap-2"
                    >
                        <span className="w-9 h-9 rounded-full bg-[#E1FF01]/10 flex items-center justify-center">
                            <Mail size={16} className="text-[#E1FF01]" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-widest text-white/50">
                                Email
                            </p>
                            <p className="text-xs font-semibold truncate">{info.email}</p>
                        </div>
                    </a>
                )}
                {info?.phone && (
                    <a
                        data-testid="support-tel"
                        href={`tel:${info.phone.replace(/\s|\(|\)/g, "")}`}
                        className="flex-1 bg-[#141414] border border-neutral-800 rounded-xl p-3 hover:border-[#E1FF01]/40 transition flex items-center gap-2"
                    >
                        <span className="w-9 h-9 rounded-full bg-[#E1FF01]/10 flex items-center justify-center">
                            <Phone size={16} className="text-[#E1FF01]" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-[10px] uppercase tracking-widest text-white/50">
                                Call
                            </p>
                            <p className="text-xs font-semibold truncate">{info.phone}</p>
                        </div>
                    </a>
                )}
            </div>

            {tab === "chat" && (
                <div className="px-5 pt-3 flex flex-col">
                    <div
                        ref={scrollRef}
                        data-testid="chat-thread"
                        className="bg-[#141414] border border-neutral-800 rounded-2xl p-3 flex-1 max-h-[55dvh] overflow-y-auto hide-scrollbar"
                    >
                        <div className="space-y-3">
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`msg-${m.role}-${i}`}
                                    className={`flex gap-2 ${
                                        m.role === "user" ? "flex-row-reverse" : ""
                                    }`}
                                >
                                    <span
                                        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                                            m.role === "user"
                                                ? "bg-[#E1FF01] text-[#0A0A0A]"
                                                : "bg-white/10 text-white"
                                        }`}
                                    >
                                        {m.role === "user" ? (
                                            <UserIcon size={14} />
                                        ) : (
                                            <Sparkles size={14} className="text-[#E1FF01]" />
                                        )}
                                    </span>
                                    <div
                                        className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                                            m.role === "user"
                                                ? "bg-[#E1FF01] text-[#0A0A0A] rounded-tr-sm"
                                                : "bg-[#0A0A0A] border border-neutral-800 rounded-tl-sm"
                                        }`}
                                    >
                                        {m.content}
                                    </div>
                                </div>
                            ))}
                            {sending && (
                                <div className="flex items-center gap-2 text-xs text-white/50">
                                    <Loader2 size={14} className="animate-spin text-[#E1FF01]" />
                                    FlexBot is typing…
                                </div>
                            )}
                        </div>
                    </div>

                    {messages.length <= 2 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {STARTER.map((s) => (
                                <button
                                    key={s.id}
                                    data-testid={`starter-${s.id}`}
                                    onClick={() =>
                                        send(
                                            s.id === "billing"
                                                ? "I have a billing question."
                                                : s.id === "pause"
                                                ? "How do I pause my subscription?"
                                                : s.id === "referral"
                                                ? "How does the referral program work?"
                                                : "Are recipes shipped or just suggestions?"
                                        )
                                    }
                                    className="text-xs uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-[#E1FF01]/40"
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <form
                        className="mt-3 flex items-end gap-2"
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                    >
                        <textarea
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            rows={1}
                            placeholder="Type your question…"
                            className="flex-1 resize-none bg-[#141414] border border-neutral-800 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#E1FF01]"
                        />
                        <button
                            data-testid="chat-send"
                            type="submit"
                            disabled={sending || !input.trim()}
                            className="bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] w-12 h-12 rounded-2xl flex items-center justify-center disabled:opacity-50"
                        >
                            <Send size={18} strokeWidth={2.5} />
                        </button>
                    </form>
                </div>
            )}

            {tab === "email" && (
                <div className="px-5 pt-4 space-y-3">
                    {!user && (
                        <div className="bg-[#141414] border border-neutral-800 rounded-xl p-4 text-sm text-white/70">
                            <p className="font-semibold text-white mb-1">Sign in to send</p>
                            <p>Sign in so we can route the reply to your inbox.</p>
                            <button
                                onClick={() => navigate("/login", { state: { next: "/support" } })}
                                className="mt-3 bg-[#E1FF01] text-[#0A0A0A] font-display text-sm uppercase tracking-widest px-4 py-2 rounded-md"
                            >
                                Sign in
                            </button>
                        </div>
                    )}
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/60">
                            Subject
                        </label>
                        <input
                            data-testid="contact-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="What's up?"
                            className="mt-1 w-full bg-[#141414] border border-neutral-800 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E1FF01]"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-white/60">
                            Message
                        </label>
                        <textarea
                            data-testid="contact-body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={6}
                            placeholder="Tell us a bit more…"
                            className="mt-1 w-full bg-[#141414] border border-neutral-800 rounded-md px-4 py-3 text-sm focus:outline-none focus:border-[#E1FF01] resize-none"
                        />
                    </div>
                    <button
                        data-testid="contact-send"
                        disabled={submitting}
                        onClick={submitEmail}
                        className="w-full bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] font-display text-xl uppercase tracking-wider py-3.5 rounded-md transition active:scale-[0.98] disabled:opacity-60"
                    >
                        {submitting ? "Sending…" : "Send Message"}
                    </button>
                    <p className="text-[10px] text-white/30 text-center uppercase tracking-widest pt-1">
                        We reply within 24 hours
                    </p>
                </div>
            )}
        </div>
    );
};

export default SupportPage;
